import { useQuery } from '@tanstack/react-query';
import { fetchWebhookErrorUnreadCount } from '~/services/webhookErrors';

const POLL_MS = 60_000;

export const WEBHOOK_ERROR_UNREAD_COUNT_KEY = ['webhook-errors', 'unread-count'] as const;

interface Options {
    /**
     * Skip polling entirely — pass false when the caller has no badge to show,
     * so we don't ask the server for a count nobody renders.
     */
    enabled?: boolean;
}

export const useWebhookErrorUnreadCount = ({ enabled = true }: Options = {}) => {
    const query = useQuery({
        queryKey: WEBHOOK_ERROR_UNREAD_COUNT_KEY,
        queryFn: fetchWebhookErrorUnreadCount,
        // authHeaders() sends the request with or without a token, so an absent
        // token would otherwise poll a 401 every minute and fill the server log
        // with `Unauthorized: /api/v2/webhook-errors/unread-count/`.
        enabled: enabled && !!localStorage.getItem('token'),
        refetchInterval: POLL_MS,
        staleTime: POLL_MS / 2,
        // Retrying a 401/403 can't help — it only multiplies each failed poll by
        // the retry count. Server and network faults are still worth one retry.
        retry: (failureCount, error: any) =>
            failureCount < 1 &&
            (error?.status === undefined || error.status >= 500),
    });
    return {
        count: query.data ?? 0,
        isLoading: query.isLoading,
        refetch: query.refetch,
    };
};
